import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FeatureHelperService } from '../_helpers/feature-helper/feature-helper.service';
import { UserService } from '../_services/user.service';
import { environment } from '../../environments/environment';

@Injectable()
export class AuthGuard  {

    constructor(
        private router: Router,
        private featureHelperService: FeatureHelperService,
        private userService: UserService
        ) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
        const stored = localStorage.getItem('currentUser');

        if (stored) {
            const user = JSON.parse(stored);

            // Seed from the cached copy first so the guard is never the reason a page renders with no
            // features at all, then refresh from the server before letting navigation through.
            this.featureHelperService.setUserFeatures(user.userFeatures);

            // Tracked so canUseGuard, which runs alongside this guard, decides on the refreshed list.
            return this.featureHelperService.trackRefresh(this.userService.getMyFeatures().pipe(
                map((features: any) => {
                    // A successful call with no rows is a real answer ("you hold nothing"), but an
                    // undefined body is not — that is a shape we did not expect, so keep the cache.
                    //
                    // The refreshed list lives in FeatureHelperService only. It must NOT be written
                    // back to localStorage['currentUser']: that object is fingerprinted by cookie "h"
                    // (SessionUserHashManagerService.checkUser, run on every API call), so rewriting it
                    // anywhere other than AuthenticationService.setUser invalidates the session and
                    // sends every subsequent request to /login. Nothing is lost by keeping it in
                    // memory — a hard reload re-seeds from the cache and refreshes again.
                    if (features) {
                        this.featureHelperService.setUserFeatures(features);
                    }
                    return true;
                }),
                // Never block navigation on this. The user is authenticated either way; a failed
                // refresh just means they keep the features they logged in with, which is exactly
                // the behaviour that existed before this call was added. Stripping their UI (or
                // bouncing them to /login) because a features call timed out would be far worse
                // than showing a slightly stale menu.
                catchError(() => of(true))
            ));
        }

        // Not logged in. Where we send them depends on the tier's default entry:
        //   ssoDefaultEntry (UAT) → straight to /signmein, which silently challenges Entra and drops
        //     a signed-in SEG user into LEAP with no page to click through.
        //   otherwise → the /login form (username/password + the manual SSO button), unchanged.
        // /login stays directly reachable either way, so switching to a different identity for
        // testing is still possible (its SSO button's checkbox forces the account picker).
        const target = environment.ssoDefaultEntry ? '/signmein' : '/login';
        this.router.navigate([target], { queryParams: { returnUrl: state.url }});
        return false;
    }
}
